import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretRight, Flask, PuzzlePiece } from '@phosphor-icons/react';
import type { Lab, Submission } from '@cvs/shared';
import { api } from '../lib/api';
import { useCourse } from '../lib/course';
import { useAsync } from '../lib/useAsync';
import { teaser } from '../components/RichText';
import { Card, EmptyState, ErrorState, LoadingState, Pill, SectionHeading, SubmissionPill, cx } from '../components/ui';

type Tab = 'projects' | 'labs' | 'exercises';

const EMPTY: Record<Tab, { title: string; description: string }> = {
  projects: { title: 'No projects yet', description: 'Section projects appear here as each section is published — you’ll submit them for grading.' },
  labs: { title: 'No labs yet', description: 'Hands-on labs appear here as classes are published — open one for the full brief.' },
  exercises: { title: 'No exercises yet', description: 'Practice exercises appear here as classes are published — short, self-checked tasks.' },
};

export function Labs() {
  const s = useAsync(() => api.labs(), []);
  const [tab, setTab] = useState<Tab>('projects');

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const { labs, submissions } = s.data;
  const projects = labs.filter((l) => l.kind === 'project');
  const hands = labs.filter((l) => l.kind === 'lab');
  const exercises = labs.filter((l) => l.kind === 'exercise');
  const shown = tab === 'projects' ? projects : tab === 'exercises' ? exercises : hands;

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Hands-on"
        title="Projects, Labs & Exercises"
        description="Capstone projects, hands-on labs and practice exercises. Open one to see the full brief."
      />

      <div className="flex w-full max-w-md gap-1 rounded-xl bg-slate-100 p-1 text-sm font-semibold">
        <TabButton active={tab === 'projects'} onClick={() => setTab('projects')} label="Projects" count={projects.length} />
        <TabButton active={tab === 'labs'} onClick={() => setTab('labs')} label="Labs" count={hands.length} />
        <TabButton active={tab === 'exercises'} onClick={() => setTab('exercises')} label="Exercises" count={exercises.length} />
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Flask} title={EMPTY[tab].title} description={EMPTY[tab].description} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {shown.map((lab) => (
            <LabCard key={lab.id} lab={lab} submission={submissions.find((sub) => sub.labId === lab.id) ?? null} />
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-1.5 transition',
        active ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-brand-navy',
      )}
    >
      {label}
      <span
        className={cx(
          'rounded-full px-1.5 text-xs tabular-nums',
          active ? 'bg-teal-50 text-brand-teal' : 'bg-slate-200 text-slate-500',
        )}
      >
        {count}
      </span>
    </button>
  );
}

const KIND_LABEL: Record<Lab['kind'], string> = {
  project: 'Section project',
  lab: 'Hands-on lab',
  exercise: 'Exercise',
};
const KIND_NOUN: Record<Lab['kind'], string> = { project: 'project', lab: 'lab', exercise: 'exercise' };

function LabCard({ lab, submission }: { lab: Lab; submission: Submission | null }) {
  const { coursePath } = useCourse();
  return (
    <Link to={coursePath(`labs/${lab.id}`)} className="group block">
      <Card className="flex h-full flex-col gap-3 p-5 transition hover:border-brand-teal/40 hover:shadow-md">
        <div className="flex items-start justify-between gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-brand-teal">
            {lab.kind === 'exercise' ? <PuzzlePiece size={20} weight="bold" /> : <Flask size={20} weight="bold" />}
          </span>
          {submission && <SubmissionPill status={submission.status} />}
        </div>

        <div>
          <h3 className="font-bold leading-snug text-brand-navy group-hover:text-brand-teal">{lab.title}</h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Pill tone="teal">{KIND_LABEL[lab.kind]}</Pill>
            {lab.env && <Pill tone="violet">{lab.env}</Pill>}
            <span className="text-xs font-semibold text-slate-500">{lab.points} pts</span>
          </div>
        </div>

        <p className="line-clamp-2 text-sm leading-relaxed text-slate-500">
          {teaser(lab.instructions) ||
            (lab.resourceUrl || lab.guideHtml ? 'Full step-by-step guide attached — open to view.' : '')}
        </p>

        <span className="mt-auto inline-flex items-center gap-1 pt-1 text-sm font-semibold text-brand-teal">
          Open {KIND_NOUN[lab.kind]}
          <CaretRight size={15} weight="bold" className="transition group-hover:translate-x-0.5" />
        </span>
      </Card>
    </Link>
  );
}
